import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WidgetCard from './WidgetCard';
import { useWidgetData } from './useWidgetData';

interface RevenueDay {
  /** YYYY-MM-DD */
  date: string;
  revenue: number | null;
}

interface RevenueResponse {
  hasReport: boolean;
  days: RevenueDay[];
  total: number;
  /** Сумма с 1-го числа текущего месяца */
  monthTotal?: number;
}

const ACCENT = '#b91c1c';
/** Высота области столбцов: блок компактный, не растягивается под высоту экрана */
const CHART_HEIGHT = 110;

const fullMoney = (v: number) => `${v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₸`;

/** Короткая подпись над столбцом: 1,27 млн / 580 тыс. */
const shortMoney = (v: number): string => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} млн`;
  if (v >= 1_000) return `${Math.round(v / 1_000).toLocaleString('ru-RU')} тыс.`;
  return String(Math.round(v));
};

const dayLabel = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const weekday = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('ru-RU', { weekday: 'short' });

const Stat: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.2 }}>{label}</Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.35, whiteSpace: 'nowrap' }}>{value}</Typography>
    {hint && <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.2 }}>{hint}</Typography>}
  </Box>
);

/** Виджет «Выручка за неделю» (кабинет аптеки). Право: home.revenue.view */
const RevenueWeekWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<RevenueResponse>('/api/v2/home/revenue/week');
  const days = data?.days || [];
  const filled = days.filter(d => d.revenue !== null);
  const max = Math.max(0, ...days.map(d => d.revenue || 0));
  const todayIso = days.length ? days[days.length - 1].date : '';
  const best = filled.length ? filled.reduce((a, b) => ((b.revenue as number) > (a.revenue as number) ? b : a)) : null;
  const avg = filled.length ? (data?.total || 0) / filled.length : 0;
  // Последний заполненный день и изменение относительно предыдущего заполненного
  const last = filled.length ? filled[filled.length - 1] : null;
  const prev = filled.length > 1 ? filled[filled.length - 2] : null;
  const deltaPct = last && prev && (prev.revenue as number) > 0
    ? Math.round((((last.revenue as number) - (prev.revenue as number)) / (prev.revenue as number)) * 100)
    : null;
  const lastHint = last
    ? `${dayLabel(last.date)}${deltaPct !== null ? ` · ${deltaPct > 0 ? '+' : ''}${deltaPct}% к пред. дню` : ''}`
    : undefined;

  return (
    <WidgetCard
      title="Выручка за неделю"
      icon={<ShowChartIcon />}
      accent={ACCENT}
      loading={loading}
      error={error}
      onRefresh={reload}
      scroll={false}
    >
      {data && !data.hasReport ? (
        <Typography sx={{ p: 2, fontSize: 13, color: '#94a3b8' }}>Для этой учетной записи нет дневного отчета</Typography>
      ) : (
        <Box
          sx={{
            display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1.5, md: 3 },
            px: 2.5, pt: 1.5, pb: 1.25
          }}
        >
          <Box
            sx={{
              display: 'flex', flexDirection: { xs: 'row', md: 'column' }, justifyContent: 'space-between',
              gap: 1.25, flexShrink: 0, minWidth: { md: 170 },
              pr: { md: 3 }, borderRight: { md: '1px solid #f1f5f9' }
            }}
          >
            <Stat label="Итого за 7 дней" value={fullMoney(data?.total || 0)} hint={filled.length ? `в среднем ${fullMoney(avg)} в день` : undefined} />
            <Stat label="Последний отчет" value={last ? fullMoney(last.revenue as number) : '-'} hint={lastHint} />
            <Stat label="С начала месяца" value={data?.monthTotal !== undefined ? fullMoney(data.monthTotal) : '-'} hint={best ? `лучший день недели: ${dayLabel(best.date)}` : undefined} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                height: CHART_HEIGHT, display: 'flex', alignItems: 'stretch', gap: { xs: 1, md: 2 },
                backgroundImage: 'linear-gradient(#f1f5f9 1px, transparent 1px)',
                backgroundSize: '100% 25%',
                borderBottom: '1px solid #e2e8f0'
              }}
            >
              {days.map(d => {
                const has = d.revenue !== null;
                const pct = has && max > 0 ? Math.max(4, ((d.revenue as number) / max) * 100) : 0;
                const isBest = best?.date === d.date;
                return (
                  <Tooltip
                    key={d.date}
                    placement="top"
                    title={has ? `${dayLabel(d.date)}: ${fullMoney(d.revenue as number)}` : `${dayLabel(d.date)}: данных нет`}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                      {has && (
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569', mb: 0.25, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                          {shortMoney(d.revenue as number)}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          width: '100%', maxWidth: 56, height: has ? `${pct}%` : 4, minHeight: 4,
                          maxHeight: CHART_HEIGHT - 18,
                          borderRadius: '6px 6px 0 0',
                          background: has
                            ? (isBest ? `linear-gradient(180deg, ${ACCENT} 0%, #7f1d1d 100%)` : 'linear-gradient(180deg, #dc6b6b 0%, #b91c1c 100%)')
                            : '#e2e8f0',
                          transition: 'height .3s ease, filter .15s',
                          '&:hover': { filter: 'brightness(0.92)' }
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, pt: 0.5 }}>
              {days.map(d => (
                <Box key={d.date} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: { xs: 10, md: 11.5 }, color: d.date === todayIso ? '#0f172a' : '#64748b', fontWeight: d.date === todayIso ? 600 : 400, whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                    {dayLabel(d.date)}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: '#94a3b8', textTransform: 'capitalize', lineHeight: 1.2 }}>{weekday(d.date)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </WidgetCard>
  );
};

export default RevenueWeekWidget;
